import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  ArrowLeft, User, DollarSign, Phone, Clock, TrendingUp, Wallet,
  ArrowDownRight, ArrowUpRight, Loader2, Save, Tag, Pause, Play,
  Eye, KeyRound, Trash2, UserX, UserCheck, Copy, RefreshCw,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatCurrency(val) { return `$${(val || 0).toFixed(2)}`; }
function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return "—"; }
}
function formatDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; }
}

const TAG_PRESETS = ["VIP", "Trial", "Enterprise", "Overdue", "High Volume", "New"];

export default function UserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [tags, setTags] = useState([]);
  const [savingNotes, setSavingNotes] = useState(false);
  const [customRate, setCustomRate] = useState("");
  const [savingRate, setSavingRate] = useState(false);
  const [balanceDialog, setBalanceDialog] = useState(false);
  const [balanceAmount, setBalanceAmount] = useState("");
  const [balanceReason, setBalanceReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [impersonateToken, setImpersonateToken] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/detail`);
      setData(res.data);
      setNotes(res.data.admin_notes || "");
      setTags(res.data.admin_tags || []);
      setCustomRate(res.data.user?.custom_rate ?? "");
    } catch (e) {
      toast.error("Failed to load user details");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.put(`${API}/admin/users/${userId}/notes`, { notes, tags });
      toast.success("Notes saved");
    } catch { toast.error("Failed to save notes"); }
    finally { setSavingNotes(false); }
  };

  const handleSaveRate = async () => {
    setSavingRate(true);
    try {
      const rateVal = customRate === "" ? null : parseFloat(customRate);
      await axios.put(`${API}/admin/users/${userId}/custom-rate`, { custom_rate: rateVal });
      toast.success(rateVal !== null ? `Custom rate set to $${rateVal}/min` : "Reset to global rate");
      fetchData();
    } catch { toast.error("Failed to set rate"); }
    finally { setSavingRate(false); }
  };

  const handleToggleBilling = async () => {
    setActionLoading(true);
    try {
      const paused = !data.user.billing_paused;
      await axios.put(`${API}/admin/users/${userId}/pause-billing`, { paused });
      toast.success(paused ? "Billing paused" : "Billing resumed");
      fetchData();
    } catch { toast.error("Failed to toggle billing"); }
    finally { setActionLoading(false); }
  };

  const handleAdjustBalance = async () => {
    const amount = parseFloat(balanceAmount);
    if (isNaN(amount) || amount === 0) { toast.error("Enter a valid amount"); return; }
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/users/${userId}/adjust-credits`, { amount, reason: balanceReason });
      toast.success("Balance adjusted");
      setBalanceDialog(false);
      setBalanceAmount("");
      setBalanceReason("");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setActionLoading(false); }
  };

  const handleImpersonate = async () => {
    try {
      const res = await axios.post(`${API}/admin/users/${userId}/impersonate`);
      setImpersonateToken(res.data.access_token);
      toast.success("Impersonation token generated");
    } catch { toast.error("Failed to impersonate"); }
  };

  const handleToggleStatus = async () => {
    setActionLoading(true);
    try {
      const endpoint = data.user.is_active === false ? "activate" : "suspend";
      await axios.post(`${API}/admin/users/${userId}/${endpoint}`);
      toast.success(endpoint === "activate" ? "User activated" : "User suspended");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setActionLoading(false); }
  };

  const toggleTag = (tag) => {
    setTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>;
  }

  if (!data) {
    return <div className="text-center py-20 text-slate-500">User not found</div>;
  }

  const u = data.user;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-[#0D1117] border border-slate-700 rounded-lg px-3 py-2 text-xs">
        <p className="text-slate-400 mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }} className="font-mono">{p.name}: {typeof p.value === "number" ? `$${p.value.toFixed(2)}` : p.value}</p>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="user-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/user-management")} className="text-slate-400 hover:text-white" data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-3 flex-1">
          <div className="h-11 w-11 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center overflow-hidden">
            {u.logo_url ? <img src={u.logo_url} alt="" className="h-full w-full object-cover" /> : <User className="h-5 w-5 text-slate-400" />}
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-100">{u.name || u.email}</h1>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>{u.email}</span>
              {u.company && <><span className="text-slate-700">|</span><span>{u.company}</span></>}
              <span className="text-slate-700">|</span>
              <span>Joined {formatDate(u.created_at)}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => fetchData()} className="flex items-center gap-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg px-3 py-1.5 transition-all" data-testid="refresh-user-detail">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /><span className="text-sm">Refresh</span>
          </button>
          {u.is_active === false ? (
            <Badge className="bg-red-500/15 text-red-400 border-red-500/20">Suspended</Badge>
          ) : (
            <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/20">Active</Badge>
          )}
          {u.billing_paused && <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/20">Billing Paused</Badge>}
          {tags.map(t => <Badge key={t} className="bg-violet-500/15 text-violet-400 border-violet-500/20 text-[10px]">{t}</Badge>)}
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Balance", value: (u.credits || 0).toLocaleString(undefined, {maximumFractionDigits: 0}), icon: DollarSign, color: "text-green-400" },
          { label: "Purchased", value: (u.total_credits_purchased || 0).toLocaleString(undefined, {maximumFractionDigits: 0}), icon: ArrowUpRight, color: "text-blue-400" },
          { label: "Used", value: (u.total_credits_used || 0).toLocaleString(undefined, {maximumFractionDigits: 0}), icon: ArrowDownRight, color: "text-amber-400" },
          { label: "Calls", value: data.total_calls, icon: Phone, color: "text-cyan-400" },
          { label: "Analyzed", value: `${data.total_minutes} min`, icon: Clock, color: "text-violet-400" },
        ].map(s => (
          <Card key={s.label} className="bg-slate-900/60 border-slate-800/50">
            <CardContent className="p-3.5">
              <div className="flex items-center gap-1.5 mb-1"><s.icon className={`h-3 w-3 ${s.color}`} /><span className="text-[10px] text-slate-500 uppercase tracking-wider">{s.label}</span></div>
              <p className={`text-lg font-bold ${s.color} font-mono`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Usage Chart */}
        <Card className="lg:col-span-2 bg-slate-900/60 border-slate-800/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="h-4 w-4 text-green-400" /> Usage (Last 30 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[180px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.usage_chart || []}>
                  <defs>
                    <linearGradient id="usageGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="spent" stroke="#f59e0b" fill="url(#usageGrad)" strokeWidth={2} name="Spent" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Admin Actions */}
        <Card className="bg-slate-900/60 border-slate-800/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Admin Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button onClick={() => { setBalanceDialog(true); setBalanceAmount(""); setBalanceReason(""); }} className="w-full justify-start bg-green-600/10 hover:bg-green-600/20 text-green-400 border border-green-500/20" variant="ghost" size="sm" data-testid="adjust-balance-btn">
              <Wallet className="h-3.5 w-3.5 mr-2" /> Adjust Minutes
            </Button>
            <Button onClick={handleToggleBilling} disabled={actionLoading} className="w-full justify-start bg-amber-600/10 hover:bg-amber-600/20 text-amber-400 border border-amber-500/20" variant="ghost" size="sm" data-testid="toggle-billing-btn">
              {u.billing_paused ? <Play className="h-3.5 w-3.5 mr-2" /> : <Pause className="h-3.5 w-3.5 mr-2" />}
              {u.billing_paused ? "Resume Billing" : "Pause Billing"}
            </Button>
            <Button onClick={handleImpersonate} className="w-full justify-start bg-blue-600/10 hover:bg-blue-600/20 text-blue-400 border border-blue-500/20" variant="ghost" size="sm" data-testid="impersonate-btn">
              <Eye className="h-3.5 w-3.5 mr-2" /> View as User
            </Button>
            <Button onClick={handleToggleStatus} disabled={actionLoading} className={`w-full justify-start border ${u.is_active === false ? "bg-emerald-600/10 hover:bg-emerald-600/20 text-emerald-400 border-emerald-500/20" : "bg-red-600/10 hover:bg-red-600/20 text-red-400 border-red-500/20"}`} variant="ghost" size="sm">
              {u.is_active === false ? <UserCheck className="h-3.5 w-3.5 mr-2" /> : <UserX className="h-3.5 w-3.5 mr-2" />}
              {u.is_active === false ? "Activate User" : "Suspend User"}
            </Button>

            {/* Custom Rate */}
            <div className="pt-2 border-t border-slate-800/50 space-y-1.5">
              <label className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Custom Rate (min/min)</label>
              <div className="flex gap-2">
                <Input type="number" step="0.001" min="0" value={customRate} onChange={(e) => setCustomRate(e.target.value)} placeholder="Global" className="bg-slate-950/60 border-slate-800/50 text-slate-100 h-8 text-xs font-mono" data-testid="custom-rate-input" />
                <Button onClick={handleSaveRate} disabled={savingRate} size="sm" className="bg-blue-600 hover:bg-blue-500 h-8 px-3">
                  {savingRate ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                </Button>
              </div>
              <p className="text-[10px] text-slate-600">Leave empty to use global rate</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Impersonate Token */}
      {impersonateToken && (
        <Card className="bg-blue-500/5 border-blue-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-400 font-medium">Impersonation Token Generated</p>
                <p className="text-xs text-slate-500 mt-0.5">Open a private browser window, navigate to the app, open DevTools Console, and run:</p>
                <code className="text-[10px] text-slate-400 mt-1 block bg-slate-950 px-2 py-1 rounded">localStorage.setItem('token', '{impersonateToken.slice(0, 30)}...')</code>
              </div>
              <Button size="sm" variant="ghost" className="text-blue-400" onClick={() => { navigator.clipboard.writeText(`localStorage.setItem('token', '${impersonateToken}'); location.reload();`); toast.success("Copied to clipboard"); }}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notes & Tags */}
      <Card className="bg-slate-900/60 border-slate-800/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2"><Tag className="h-4 w-4" /> Notes & Tags</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {TAG_PRESETS.map(t => (
              <button key={t} onClick={() => toggleTag(t)} className={`px-2.5 py-1 rounded-full text-[10px] font-medium border transition ${tags.includes(t) ? "bg-violet-500/20 border-violet-500/30 text-violet-400" : "bg-slate-800/50 border-slate-700/50 text-slate-500 hover:text-slate-300"}`}>
                {t}
              </button>
            ))}
          </div>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Admin notes about this user..." className="bg-slate-950/60 border-slate-800/50 text-slate-200 text-sm min-h-[80px]" data-testid="admin-notes" />
          <Button onClick={handleSaveNotes} disabled={savingNotes} size="sm" className="bg-blue-600 hover:bg-blue-500">
            {savingNotes ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />} Save Notes
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Category Breakdown */}
        <Card className="bg-slate-900/60 border-slate-800/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Call Category Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(data.category_breakdown || {}).length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">No calls analyzed yet</p>
            ) : (
              <div className="space-y-2">
                {Object.entries(data.category_breakdown).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
                  <div key={cat} className="flex items-center justify-between">
                    <span className="text-xs text-slate-400 capitalize">{cat.replace(/_/g, " ")}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.min(100, (count / data.total_calls) * 100)}%` }} />
                      </div>
                      <span className="text-xs font-mono text-slate-300 w-8 text-right">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Transactions */}
        <Card className="bg-slate-900/60 border-slate-800/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Recent Billing Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            {data.transactions.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">No transactions yet</p>
            ) : (
              <div className="space-y-1.5 max-h-[250px] overflow-y-auto pr-1">
                {data.transactions.slice(0, 25).map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between bg-slate-950/40 rounded-lg px-3 py-2 border border-slate-800/20">
                    <div className="flex items-center gap-2">
                      {tx.amount < 0 ? <ArrowDownRight className="h-3 w-3 text-red-400" /> : <ArrowUpRight className="h-3 w-3 text-green-400" />}
                      <div>
                        <p className="text-[11px] text-slate-300">{tx.description}</p>
                        <p className="text-[9px] text-slate-600">{formatDateTime(tx.created_at)}</p>
                      </div>
                    </div>
                    <span className={`text-xs font-mono ${tx.amount < 0 ? "text-red-400" : "text-green-400"}`}>
                      {tx.amount < 0 ? "-" : "+"}{Math.abs(tx.amount).toFixed(2)} cr
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Calls */}
      <Card className="bg-slate-900/60 border-slate-800/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2"><Phone className="h-4 w-4" /> Recent Calls ({data.total_calls} total)</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800/50">
                  {["Date", "Publisher", "Category", "Duration", "Confidence", "Status"].map(h => (
                    <th key={h} className="text-left text-[10px] text-slate-500 uppercase tracking-wider font-medium px-4 py-2">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.recent_calls.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-8 text-slate-500 text-sm">No calls</td></tr>
                ) : (
                  data.recent_calls.map((c) => (
                    <tr key={c.id} className="border-b border-slate-800/20 hover:bg-slate-800/10">
                      <td className="px-4 py-2 text-xs text-slate-400">{formatDate(c.call_date)}</td>
                      <td className="px-4 py-2 text-xs text-slate-300">{c.publisher_name || "—"}</td>
                      <td className="px-4 py-2">
                        <Badge className={`text-[9px] ${c.category === "valid_service_call" ? "bg-emerald-500/15 text-emerald-400" : c.category === "adult_call" ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400"}`}>
                          {(c.category || "pending").replace(/_/g, " ")}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-400 font-mono">{c.call_duration ? `${Math.round(c.call_duration / 60)}m` : "—"}</td>
                      <td className="px-4 py-2 text-xs text-slate-400 font-mono">{c.confidence_score ? `${c.confidence_score}%` : "—"}</td>
                      <td className="px-4 py-2">
                        <Badge className={`text-[9px] ${c.status === "analyzed" ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-500/15 text-slate-400"}`}>{c.status || "pending"}</Badge>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Balance Dialog */}
      <Dialog open={balanceDialog} onOpenChange={setBalanceDialog}>
        <DialogContent className="bg-[#11141D] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Adjust Minutes</DialogTitle>
            <DialogDescription className="text-slate-400">Current: {(u.credits || 0).toLocaleString()} minutes</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Input type="number" step="0.01" value={balanceAmount} onChange={(e) => setBalanceAmount(e.target.value)} placeholder="e.g. 50.00 or -10.00" className="bg-slate-950/60 border-slate-800/50 text-slate-100" data-testid="detail-balance-amount" />
            <Input value={balanceReason} onChange={(e) => setBalanceReason(e.target.value)} placeholder="Reason (optional)" className="bg-slate-950/60 border-slate-800/50 text-slate-100" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBalanceDialog(false)} className="text-slate-400">Cancel</Button>
            <Button onClick={handleAdjustBalance} disabled={actionLoading} className="bg-green-600 hover:bg-green-500">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
